import { Command } from 'commander';
import { promises as fs } from 'fs';
import { join } from 'path';
import { select, confirm, input } from '@inquirer/prompts';
import { ConfigService } from '../../services/config.service.js';
import { ProjectConfigService, type RulesConfig } from '../../services/project-config.service.js';
import { ValidationError } from '../../errors/index.js';
import { selectTheme } from '../../utils/prompt-theme.js';

interface Preset {
    slug: string;
    name: string;
    description: string;
    version: string;
    tags: string[];
    stack: {
        frontend?: string | null;
        backend?: string | null;
        css?: string | null;
        database?: string | null;
        language?: string | null;
    };
    downloads: number;
}

interface PresetBundle {
    preset: {
        slug: string;
        name: string;
        description: string;
        version: string;
        tags: string[];
        stack: Record<string, string | null>;
        files: {
            required: string[];
            optional: string[];
            template: string[];
        };
        output?: {
            agentsMd?: boolean;
            ideFolder?: string;
        };
    };
    files: Record<string, string>;
}



export function createRulesInitCommand(): Command {
    return new Command('init')
        .description('Apply rule preset to project')
        .option('--preset <slug>', 'Preset slug to apply')
        .option('--output <format>', 'Output format: cursor, agents-md, both (default: cursor)', 'cursor')
        .option('-y, --yes', 'Skip confirmations')
        .action(async (options) => {
            const configService = new ConfigService();
            const config = await configService.load();

            if (!config) {
                throw new ValidationError('Not initialized. Run "jai1 auth" first.');
            }

            let presetSlug = options.preset as string | undefined;

            // If no preset specified, show interactive selection
            if (!presetSlug) {
                console.log('📋 Fetching available presets...\n');

                const response = await fetch(`${config.apiUrl}/api/rules/presets`, {
                    headers: {
                        'JAI1-Access-Key': config.accessKey,
                    },
                });

                if (!response.ok) {
                    throw new Error(`Failed to fetch presets: ${response.statusText}`);
                }

                const data = (await response.json()) as { presets: Preset[]; total: number };

                if (data.total === 0) {
                    console.log('No presets available.');
                    return;
                }

                presetSlug = await select({
                    message: 'Select a preset:',
                    choices: data.presets.map((p) => ({
                        name: `${p.name} - ${p.description}`,
                        value: p.slug,
                        description: `v${p.version} | ${p.tags.join(', ')}`,
                    })),
                    theme: selectTheme,
                });
            }

            // Fetch preset bundle
            console.log(`\n📦 Fetching preset: ${presetSlug}...\n`);

            const presetResponse = await fetch(`${config.apiUrl}/api/rules/presets/${presetSlug}`, {
                headers: {
                    'JAI1-Access-Key': config.accessKey,
                },
            });

            if (!presetResponse.ok) {
                if (presetResponse.status === 404) {
                    throw new Error(`Preset '${presetSlug}' not found`);
                }
                throw new Error(`Failed to fetch preset: ${presetResponse.statusText}`);
            }

            const bundle = (await presetResponse.json()) as PresetBundle;

            console.log(`✓ Preset: ${bundle.preset.name} v${bundle.preset.version}`);
            console.log(`  Files: ${Object.keys(bundle.files).length}`);

            // Determine output format
            let outputFormat = options.output as string;
            if (!['cursor', 'agents-md', 'both'].includes(outputFormat)) {
                outputFormat = await select({
                    message: 'Select output format:',
                    choices: [
                        { name: 'Cursor (.cursor/rules/)', value: 'cursor' },
                        { name: 'AGENTS.md (single file)', value: 'agents-md' },
                        { name: 'Both', value: 'both' },
                    ],
                    theme: selectTheme,
                });
            }

            // Confirm before applying
            if (!options.yes) {
                const proceed = await confirm({
                    message: `Apply preset '${bundle.preset.name}' to current directory?`,
                    default: true,
                });

                if (!proceed) {
                    console.log('Cancelled.');
                    return;
                }
            }

            console.log('\n📝 Applying preset...\n');

            // Apply based on output format
            if (outputFormat === 'cursor' || outputFormat === 'both') {
                await applyCursorFormat(bundle);
            }

            if (outputFormat === 'agents-md' || outputFormat === 'both') {
                await applyAgentsMdFormat(bundle);
            }

            // Create project config
            const projectConfigService = new ProjectConfigService();
            const rulesConfig: RulesConfig = {
                preset: bundle.preset.slug,
                version: bundle.preset.version,
                appliedAt: new Date().toISOString(),
                ides: [],
                customContext: '09-custom.mdc',
            };

            await projectConfigService.saveRules(rulesConfig);
            console.log('✓ Created .jai1/project.json');

            console.log('\n✅ Preset applied successfully!\n');
            console.log('Next steps:');
            console.log('  1. Edit 09-custom.mdc to add project-specific rules');
            console.log('  2. Commit the rules to git');
            console.log('  3. Use "jai1 rules sync" to regenerate outputs after editing\n');
        });
}

async function applyCursorFormat(bundle: PresetBundle): Promise<void> {
    const rulesDir = join(process.cwd(), '.cursor', 'rules');

    // Create directory
    await fs.mkdir(rulesDir, { recursive: true });

    // Write all files
    for (const [filename, content] of Object.entries(bundle.files)) {
        const filePath = join(rulesDir, filename);
        await fs.writeFile(filePath, content, 'utf-8');
        console.log(`✓ Created .cursor/rules/${filename}`);
    }
}

async function applyAgentsMdFormat(bundle: PresetBundle): Promise<void> {
    const sections: string[] = [];

    // Header
    sections.push('# AGENTS.md\n');
    sections.push(`<!-- Generated by jai1 rules - Preset: ${bundle.preset.slug} v${bundle.preset.version} -->\n`);

    // Extract content from each file (remove frontmatter)
    const fileOrder = [
        '01-project.mdc',
        '02-standards.mdc',
        '03-frontend.mdc',
        '04-backend.mdc',
        '05-testing.mdc',
        '06-workflow.mdc',
        '09-custom.mdc',
    ];

    for (const filename of fileOrder) {
        if (bundle.files[filename]) {
            const content = bundle.files[filename];
            // Remove frontmatter (---\n...\n---)
            const withoutFrontmatter = content.replace(/^---\n[\s\S]*?\n---\n/, '');
            sections.push(withoutFrontmatter.trim());
            sections.push('\n');
        }
    }

    const agentsMd = sections.join('\n');
    await fs.writeFile('AGENTS.md', agentsMd, 'utf-8');
    console.log('✓ Created AGENTS.md');
}
